import { makeAutoObservable, computed, runInAction } from 'mobx'
import { Message, Reaction } from '../Contracts'
import { LoadChatMessagesTask } from '../Tasks/LoadChatMessagesTask'
import { SearchChatMessagesTask } from '../Tasks/SearchChatMessagesTask'
import { SendMessageTask } from '../Tasks/SendMessageTask'
import { UploadChatFileTask } from '../Tasks/UploadChatFileTask'
import { EditMessageTask } from '../Tasks/EditMessageTask'
import { DeleteMessageTask } from '../Tasks/DeleteMessageTask'
import { ToggleReactionTask } from '../Tasks/ToggleReactionTask'
import { attachmentPreview } from '../Helpers/attachmentPreview'
import { stripHtml } from '../Helpers/stripHtml'
import type { RootStore } from './index'

/** Server search needs at least this many characters (mirrors the backend rule) */
export const SEARCH_MIN_QUERY_LENGTH = 2

export class MessageStore {
    messages: Message[] = []
    /**
     * Cursor pagination state per chat: does the backend have older messages
     * than the oldest loaded one? Replaces the client-side loadedCountMap of
     * the mock version — with a real backend every loaded message is visible
     * and older pages are fetched with the `before` cursor.
     */
    hasMoreMap: Map<string, boolean> = new Map()
    /**
     * Chats whose history has been fetched from the backend. A chat may
     * contain realtime-arrived messages before its history is loaded, so
     * "has messages" must not be used as "is loaded".
     */
    loadedChats: Set<string> = new Set()
    isLoadingHistory = false
    isLoading = false
    /** Send in flight (upload + send) — guards against a double submit */
    isSending = false
    /** Server search results for the active chat, newest first */
    searchResults: Message[] = []
    isSearching = false
    error: string | null = null

    private rootStore: RootStore
    /** In-flight older-page request — shared so concurrent callers await one fetch */
    private loadMorePromise: Promise<boolean> | null = null
    /** Chat the in-flight request belongs to — a caller for another chat must not adopt it */
    private loadMoreChatId: string | null = null
    /** Monotonic counter: a stale search response must not overwrite a newer one */
    private searchSeq = 0

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            activeChatMessages: computed,
        })
        this.rootStore = rootStore
    }

    async loadMessages(chatId: string) {
        this.isLoading = true
        this.error = null
        try {
            const { messages, hasMore } = await LoadChatMessagesTask({ chatId })
            runInAction(() => {
                // Remove old messages for this chat, add the fresh first page
                this.messages = [...this.messages.filter((m) => m.chatId !== chatId), ...messages]
                this.hasMoreMap.set(chatId, hasMore)
                this.loadedChats.add(chatId)
            })
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to load messages'
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    get activeChatMessages(): Message[] {
        const chatId = this.rootStore.uiStore.activeChatId
        if (!chatId) return []

        return this.messages
            .filter((m) => m.chatId === chatId)
            .sort((a, b) => a.timestamp - b.timestamp)
    }

    /**
     * Server-side search over ALL messages of the chat (the previous
     * client-side filter only saw the loaded history window). Responses
     * may arrive out of order — only the latest request may apply.
     */
    async searchMessages(chatId: string, query: string) {
        const seq = ++this.searchSeq
        const trimmed = query.trim()

        if (trimmed.length < SEARCH_MIN_QUERY_LENGTH) {
            this.searchResults = []
            this.isSearching = false
            return
        }

        this.isSearching = true
        try {
            const results = await SearchChatMessagesTask({ chatId, query: trimmed })
            runInAction(() => {
                if (seq !== this.searchSeq) return
                this.searchResults = results
                this.isSearching = false
            })
        } catch (e) {
            runInAction(() => {
                if (seq !== this.searchSeq) return
                this.searchResults = []
                this.isSearching = false
                this.error = e instanceof Error ? e.message : 'Failed to search messages'
            })
        }
    }

    clearSearchResults() {
        this.searchSeq++
        this.searchResults = []
        this.isSearching = false
    }

    isChatLoaded(chatId: string): boolean {
        return this.loadedChats.has(chatId)
    }

    hasMoreMessages(chatId: string): boolean {
        return this.hasMoreMap.get(chatId) ?? false
    }

    /** @returns чи завантажилась сторінка (false — помилка мережі або нема старіших) */
    loadMoreMessages(chatId: string): Promise<boolean> {
        // Concurrent callers (scroll sentinel + ensureMessageVisible) share
        // the in-flight request instead of firing duplicates. A request for
        // ANOTHER chat is not ours to adopt: wait it out, then start our own
        if (this.loadMorePromise) {
            if (this.loadMoreChatId === chatId) return this.loadMorePromise
            return this.loadMorePromise.then(() => this.loadMoreMessages(chatId))
        }
        if (!this.hasMoreMessages(chatId)) return Promise.resolve(false)

        const loaded = this.messages
            .filter((m) => m.chatId === chatId)
            .sort((a, b) => a.timestamp - b.timestamp)
        const oldest = loaded[0]
        if (!oldest) return Promise.resolve(false)

        this.isLoadingHistory = true
        this.loadMoreChatId = chatId
        this.loadMorePromise = (async () => {
            try {
                const { messages, hasMore } = await LoadChatMessagesTask({
                    chatId,
                    before: oldest.id,
                })
                runInAction(() => {
                    const known = new Set(this.messages.map((m) => m.id))
                    this.messages.push(...messages.filter((m) => !known.has(m.id)))
                    this.hasMoreMap.set(chatId, hasMore)
                })
                return true
            } catch (e) {
                runInAction(() => {
                    this.error = e instanceof Error ? e.message : 'Failed to load messages'
                })
                return false
            } finally {
                runInAction(() => {
                    this.isLoadingHistory = false
                })
                this.loadMorePromise = null
                this.loadMoreChatId = null
            }
        })()

        return this.loadMorePromise
    }

    /**
     * Pulls older pages until the message is in the loaded window — a search
     * hit may lie far beyond the visible history. Capped: a hit deep in a
     * years-long chat would otherwise fetch hundreds of pages; when the cap
     * is hit the caller simply cannot scroll to it (the hit stays visible
     * in the search panel). Returns whether the message is now loaded.
     */
    async ensureMessageVisible(chatId: string, messageId: string): Promise<boolean> {
        const MAX_PAGES = 10

        for (let i = 0; i < MAX_PAGES; i++) {
            if (this.messages.some((m) => m.id === messageId)) return true
            if (!this.hasMoreMessages(chatId)) return false

            const pageLoaded = await this.loadMoreMessages(chatId)
            if (!pageLoaded) {
                // Помилка мережі: без ретраїв — інакше один клік по результату
                // пошуку перетворюється на чергу з MAX_PAGES фейл-запитів
                return false
            }
        }

        return this.messages.some((m) => m.id === messageId)
    }

    async sendMessage(chatId: string, content: string, attachments?: File[], replyToId?: string) {
        if (this.isSending) return undefined
        this.isSending = true

        try {
            // Two-step attachment flow: upload raw files first, then send
            // the message referencing the returned uuids — the backend
            // attaches them atomically with the message itself
            let fileUuids: string[] | undefined
            if (attachments?.length) {
                const uploaded = await Promise.all(attachments.map((file) => UploadChatFileTask(file)))
                fileUuids = uploaded.map((item) => item.uuid)
            }

            const newMessage = await SendMessageTask({
                chatId,
                content,
                ...(fileUuids?.length ? { fileUuids } : {}),
                ...(replyToId ? { replyToId } : {}),
            })

            runInAction(() => {
                this.messages.push(newMessage)
            })

            const displayText = stripHtml(content) || attachmentPreview(newMessage.files)
            this.rootStore.chatStore.updateLastMessage(chatId, displayText, newMessage.senderId, newMessage.timestamp)

            return newMessage
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to send message'
            })
            return undefined
        } finally {
            runInAction(() => {
                this.isSending = false
            })
        }
    }

    async deleteMessage(messageId: string) {
        const message = this.messages.find((m) => m.id === messageId)
        if (!message) return

        try {
            await DeleteMessageTask(messageId)
            runInAction(() => {
                // Спільний хелпер зі realtime-гілкою: чистить і стрічку,
                // і знімок пошуку (там окремі інстанси повідомлень)
                this.applyMessageDeleted(messageId)
            })
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Failed to delete message'
            })
        }
    }

    async editMessage(messageId: string, newContent: string) {
        const message = this.messages.find((m) => m.id === messageId)
        if (!message) return

        const oldContent = message.content
        const oldIsEdited = message.isEdited
        // Optimistic update (стрічка + знімок пошуку)
        runInAction(() => {
            this.applyMessageUpdated(messageId, newContent, true)
        })

        try {
            await EditMessageTask({ messageId, content: newContent })
        } catch (e) {
            // Rollback on failure
            runInAction(() => {
                this.applyMessageUpdated(messageId, oldContent, oldIsEdited)
                this.error = e instanceof Error ? e.message : 'Failed to edit message'
            })
        }
    }

    /**
     * Optimistic toggle: mutate locally right away, then replace with the
     * server aggregate (single source of truth) or roll back on failure.
     */
    async toggleReaction(messageId: string, emoji: string) {
        const message = this.messages.find((m) => m.id === messageId)
        if (!message || message.isDeleted) return

        const previous = message.reactions?.map((r) => ({ emoji: r.emoji, userIds: [...r.userIds] }))

        const userId = this.rootStore.userStore.currentUserId
        if (!message.reactions) message.reactions = []

        const existing = message.reactions.find((r) => r.emoji === emoji)
        if (existing) {
            if (existing.userIds.includes(userId)) {
                existing.userIds = existing.userIds.filter((id) => id !== userId)
                if (existing.userIds.length === 0) {
                    message.reactions = message.reactions.filter((r) => r.emoji !== emoji)
                }
            } else {
                existing.userIds.push(userId)
            }
        } else {
            message.reactions.push({ emoji, userIds: [userId] })
        }

        if (message.reactions.length === 0) {
            // exactOptionalPropertyTypes: remove the key instead of = undefined
            delete message.reactions
        }

        // Стан одразу після optimistic-мутації: маркер «ніхто не встиг змінити»
        const optimisticSnapshot = JSON.stringify(message.reactions ?? [])

        try {
            const reactions = await ToggleReactionTask(messageId, emoji)
            runInAction(() => {
                this.applyReactions(messageId, reactions)
            })
        } catch (e) {
            runInAction(() => {
                // Відкат лише якщо за час запиту не прилетів message:reaction:
                // broadcast — це вже серверна правда (зокрема нашого ж кліку,
                // чия REST-відповідь загубилась), і затирати її снапшотом не можна
                if (JSON.stringify(message.reactions ?? []) === optimisticSnapshot) {
                    if (previous) {
                        message.reactions = previous
                    } else {
                        delete message.reactions
                    }
                }
                this.error = e instanceof Error ? e.message : 'Failed to toggle reaction'
            })
        }
    }

    /** Realtime message:reaction / server aggregate after own toggle */
    applyReactions(messageId: string, reactions: Reaction[]) {
        const message = this.messages.find((m) => m.id === messageId)
        if (!message) return

        if (reactions.length > 0) {
            message.reactions = reactions
        } else {
            delete message.reactions
        }
    }

    getMessageById(id: string): Message | undefined {
        return this.messages.find((m) => m.id === id)
    }

    /** Чат зник (видалено/вигнали/вийшов) — чистимо його сліди у сторі */
    removeChatMessages(chatId: string) {
        this.messages = this.messages.filter((m) => m.chatId !== chatId)
        this.loadedChats.delete(chatId)
        this.hasMoreMap.delete(chatId)
    }

    /** Realtime: message:new for a chat whose history may or may not be loaded */
    applyIncomingMessage(message: Message) {
        if (this.messages.some((m) => m.id === message.id)) return
        this.messages.push(message)
    }

    /**
     * Realtime: message:updated. Знімок пошуку тримає ОКРЕМІ інстанси
     * повідомлень (mapApiMessage створює нові об'єкти), тому оновлюється
     * обидва — інакше панель пошуку показувала б старий текст.
     */
    applyMessageUpdated(messageId: string, content: string, isEdited: boolean) {
        const message = this.messages.find((m) => m.id === messageId)
        if (message) {
            message.content = content
            message.isEdited = isEdited
        }

        const searchHit = this.searchResults.find((m) => m.id === messageId)
        if (searchHit) {
            searchHit.content = content
            searchHit.isEdited = isEdited
        }
    }

    /**
     * Realtime: message:deleted (soft-delete stub, mirrors the backend).
     * З результатів пошуку видалене прибирається зовсім — бек фільтрує
     * is_deleted, і залишений знімок світив би текст видаленого повідомлення.
     */
    applyMessageDeleted(messageId: string) {
        const message = this.messages.find((m) => m.id === messageId)
        if (message) {
            message.isDeleted = true
            message.content = ''
        }

        if (this.searchResults.some((m) => m.id === messageId)) {
            this.searchResults = this.searchResults.filter((m) => m.id !== messageId)
        }
    }
}
